import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle2, ClipboardList, Fuel, CalendarDays, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * Clerk login reminder.
 *
 * Fires once per browser session (so every fresh login triggers it,
 * but it doesn't keep popping up while the clerk navigates).
 */
const SESSION_KEY = 'clerk_login_reminder_shown';

const ITEMS = [
  { icon: Mail,          color: 'text-blue-600',    title: 'Check your work emails',
    detail: 'Reply to anything client-facing first, then internal.' },
  { icon: ShieldCheck,   color: 'text-rose-600',    title: 'Review pending fuel approvals',
    detail: 'Open Fuel Tracker → Pending Approvals tab if the badge is red.',
    href: '/admin/fuel-tracker' },
  { icon: CalendarDays,  color: 'text-violet-600',  title: "Capture today's staff attendance",
    detail: 'Mark ON / OFF / LEAVE / OT / SAT / PH for every team member.',
    href: '/admin/company-register' },
  { icon: Fuel,          color: 'text-amber-600',   title: "Capture today's fuel",
    detail: 'Every entry needs a reference (invoice / receipt no.).',
    href: '/admin/fuel-tracker' },
  { icon: ClipboardList, color: 'text-emerald-600', title: 'Capture any new applications',
    detail: 'Drivers + owner-drivers walking in — log them in Applications.',
    href: '/admin/applications' },
];

export default function ClerkLoginReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'clerk') return;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    setOpen(true);
    sessionStorage.setItem(SESSION_KEY, '1');
  }, [user]);

  if (!user || user.role !== 'clerk') return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="clerk-reminder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Daily Reminders
          </DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">
            Hi {user.full_name || user.username || 'there'} — quick checklist before you start.
          </p>
        </DialogHeader>
        <ul className="space-y-3">
          {ITEMS.map(({ icon: Icon, color, title, detail, href }, i) => (
            <li
              key={i}
              className={`flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 ${href ? 'cursor-pointer hover:bg-slate-50 transition-colors' : ''}`}
              onClick={() => { if (href) { setOpen(false); navigate(href); } }}
              data-testid={`reminder-item-${i}`}
            >
              <Icon className={`h-5 w-5 ${color} mt-0.5 shrink-0`} />
              <div className="text-sm">
                <p className="font-semibold text-slate-900">{title}</p>
                <p className="text-xs text-slate-600">{detail}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="pt-2 flex justify-end">
          <Button onClick={() => setOpen(false)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="dismiss-reminder-btn">
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
